
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { translations, Language, TranslationKeys } from '@/translations/okx';

type LangContextType = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: TranslationKeys;
  isLoading: boolean;
};

const defaultLanguage: Language = 'en';

// Create context with default values
const LangContext = createContext<LangContextType>({
  lang: defaultLanguage,
  setLang: () => {},
  t: translations[defaultLanguage],
  isLoading: true,
});

export const useLang = () => useContext(LangContext);

interface LangProviderProps {
  children: ReactNode;
}

export const LangProvider = ({ children }: LangProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLangState] = useState<Language>(defaultLanguage);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize language from URL parameter or localStorage with OKX-specific key
  useEffect(() => {
    const initializeLang = () => {
      try {
        const urlLang = searchParams.get('lang') as Language;
        const savedLang = localStorage.getItem('okx_prefLang') as Language;
        
        // Validate language code
        const isValidLang = (code: string): code is Language => 
          code !== null && code in translations;
        
        if (isValidLang(urlLang)) {
          setLangState(urlLang);
          localStorage.setItem('okx_prefLang', urlLang);
        } else if (isValidLang(savedLang)) {
          setLangState(savedLang);
        }
        
        console.log('Language initialized:', urlLang || savedLang || defaultLanguage);
      } catch (error) {
        console.error('Error initializing language:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeLang();
  }, [searchParams]);

  // Update language and save to localStorage with OKX-specific key
  const setLang = (newLang: Language) => {
    if (translations[newLang]) {
      console.log('Setting language to:', newLang);
      setLangState(newLang);
      localStorage.setItem('okx_prefLang', newLang);
      
      // Update URL parameter
      searchParams.set('lang', newLang);
      setSearchParams(searchParams);
    }
  };

  // Get current translations based on selected language
  const t = translations[lang] as TranslationKeys;

  return (
    <LangContext.Provider value={{ lang, setLang, t, isLoading }}>
      {children}
    </LangContext.Provider>
  );
};
